import QueryString from 'qs';

import { jsonApi } from '../client';
import type { Column } from '../column/types';
import { filterViewRequest } from '../helpers/view';
import { generateResourceScoutApi } from '../scout/api';
import { shareRelationApi } from '../share/relation';
import type { PaginationQuery, PaginationResource } from '../types/pagination';
import type { ColumnQuery, ResourceViewApi } from '../types/resource-api';
import type { StoreViewRequest, UpdateViewRequest, View, ViewTab } from './types';

const view = jsonApi('view');

export const generateResourceViewApi = (resourceName: string): ResourceViewApi => {
  const resourceView = jsonApi(`${resourceName}/view`);

  const index = (): Promise<View[]> => {
    return resourceView.get();
  };

  const def = (): Promise<View> => {
    return resourceView.get('default');
  };

  const empty = (): Promise<View> => {
    return resourceView.get('empty');
  };

  const tabs = (): Promise<ViewTab[]> => {
    return resourceView.get('tabs');
  };

  const availableColumns = (query?: ColumnQuery): Promise<Column[]> => {
    return resourceView.get('available-columns', {
      searchParams: QueryString.stringify(query),
    });
  };

  return {
    index,
    default: def,
    empty,
    tabs,
    availableColumns,
  };
};

const index = (query?: PaginationQuery): Promise<PaginationResource<View>> => {
  return view.get(undefined, {
    searchParams: QueryString.stringify(query),
  });
};

const show = (id: string | number): Promise<View> => {
  return view.get(id.toString());
};

const store = (data: StoreViewRequest): Promise<View> => {
  return view.post(undefined, filterViewRequest(data));
};

const update =
  (id: string | number) =>
  (data: UpdateViewRequest): Promise<unknown> => {
    return view.put(id.toString(), filterViewRequest(data));
  };

const addTab = (id: string | number): Promise<unknown> => {
  return view.put(`${id}/tab/add`);
};

const removeTab = (id: string | number): Promise<unknown> => {
  return view.put(`${id}/tab/remove`);
};

const destroy = (id: string | number): Promise<unknown> => {
  return view.delete(id.toString());
};

const favorite = (id: string | number): Promise<unknown> => {
  return view.post(`${id}/favorite`);
};

export const viewApi = {
  name: 'view',
  index,
  show,
  store,
  update,
  addTab,
  removeTab,
  destroy,
  favorite,
  view: generateResourceViewApi('view'),
  scout: generateResourceScoutApi<View>('view', (item) => ({
    label: item.name,
    value: item.id,
  })),
  relation: {
    share: shareRelationApi('view'),
  },
};
